// reference-creation.ts — validation of catalogue reference creation.
//
// Two doors, one validation: the panel (single creations) and the batch import both call this
// module, with no bypass. Pure function, with the context injected.
//
// Three kinds of field:
//   - blocking: required fields, no creation while any remains;
//   - pending: the reference is created but inactive and hidden from menus (missing CRM ref, or a
//     new category needing a supervisor decision);
//   - informative: reported, blocks nothing (an uncollected PA grid, where the quote shows the
//     target PA).
// Inactive is for refs that cannot live in a quote at all, never for those living with an
// estimated margin; incomplete information is reported instead.
//
// The reference is auto-incremented: the category gives the family, hence the prefix, hence the
// next ref in the series. The panel computes it read-only; the import may carry explicit refs, and
// the validator checks they do not break the series.
//
// UPZ duplication is automatic: ticking in-house stock creates the pair, standard plus its "U"
// twin, with the same values and prefixed labels.

import { CATALOGUE, COMPAT, MATERIAL_FAMILY, STD_PRINT_MAP, PRINTS, CUTS, LAMINATIONS, FINISHINGS, PA_SUPPLIERS, MATERIAL_REFS, engineSettings } from "../../data";
import { DEFAULT_WASTE_COEFF } from "../../data/calibration";
import { materialNatureOf, isUpz, type MaterialNature } from "../pricing/pa-material";
import { materialFamilyOf, itemAllowed, compatibleOptions } from "./compatibility";
import { menusImport, type MenuItem } from "../production/import-menus";
import type { Compat } from "../../models/data.model";
import type { PaMode } from "../pricing/pa-margins";
import { num } from "../core/core";
import { engineMessage, keyParam } from "../core/messages";

/**
 * Numbering floor of the €/m² reference range, from 1001 (accessories at €/linear metre start at 2001).
 * ENGINE INVARIANT, not a business parameter: it is the reference numbering scheme PREFIX-NNNN baked into
 * every persisted reference code and into the catalogue series; changing it would renumber history.
 */
export const M2_RANGE = 1001;

/** Material draft (panel or batch row). Text fields are tolerated; the validator normalizes them. */
export interface MaterialDraft {
  /** Ref, auto-computed by the panel and optionally explicit on import. Empty means series-computed. */
  ref: string;
  /** Cumul category (closed list; a new one is a supervisor decision). Determines the family. */
  category: string;
  /** Explicit family, used only for a new category; otherwise derived from the category. */
  family17?: string;
  itemShort: string;
  fullName: string;
  weightGsm: number | string | null;
  pvM2: number | string | null;
  minFlatFee: number | string | null;
  minUnitPrice: number | string | null;
  standardPrint: string;
  formats: readonly string[];
  defaultFormat: string;
  /** Distributor we buy from (SDAG, STARCOLORS, ...). */
  distributor: string;
  /** Brand, i.e. the manufacturer (AVERY, HEXIS, ...). Also used in labels. */
  brand: string;
  purchasePaM2: number | string | null;
  /** Waste coefficient (fraction 0..1); defaults by material nature when empty. */
  wasteCoeff?: number | string | null;
  /** In-house stock: when ticked, also creates the "U" twin as an atomic pair. */
  stockUpz: boolean;
  /** Sellsy CRM ref. Empty means pending, hence inactive. */
  refCrm: string;
  /** Is the PA grid collected? Informative only: blocks nothing and does not force inactive. */
  collectedPaGrid?: boolean;
}

/** Injected context: the current catalogue state, for uniqueness and series. */
export interface CreationContext {
  existingRefs: readonly string[];
  existingCategories: readonly string[];
  /** Roots reserved by provisional materials (AD-1042-NEW reserves "AD-1042"): an explicit ref
   *  equal to one of them materializes it rather than breaking the series. */
  reservedRoots?: readonly string[];
}

/** Normalized material, ready to persist. */
export interface NormalizedMaterial {
  ref: string;
  name: string;
  fullName: string;
  category: string;
  pv: number;
  min_flat_fee: number;
  min_unit_price: number;
  standardPrint: string;
  kg: number;
  isMoq: false;
  family17: string;
  formats: string[];
  defaultFormat: string;
  distributor: string;
  brand: string;
  purchasePa: number;
  /** Chosen waste coefficient; null keeps the family default instead of writing a specific one. */
  wasteCoeff: number | null;
  stockUpz: boolean;
  /** True when persisting also creates the "U" twin (see materialTwins). */
  withTwinUpz: boolean;
}

/** Result of a material creation validation. */
export interface CreationResult {
  /** Blocking reasons (French, shown to the user); no creation while any remains. */
  blockers: string[];
  /** Reasons the reference would be created inactive. */
  pending: string[];
  /** Informative notices; they affect neither the creation nor the status. */
  informations: string[];
  proposedStatus: "active" | "inactive";
  creatable: boolean;
  normalized: NormalizedMaterial | null;
}

/**
 * Series prefix of a reference (leading letters, uppercased).
 * @param ref The reference.
 * @returns The prefix, or an empty string.
 */
function refPrefix(ref: string): string {
  const m = /^([A-Za-z]+)/.exec(ref.trim());
  return m ? m[1]!.toUpperCase() : "";
}

/**
 * Sequence number of a reference such as "AD-1042".
 * @param ref The reference.
 * @returns The number, or null when the format is not PREFIX-NNNN.
 */
function refNumber(ref: string): number | null {
  const m = /^[A-Za-z]+-(\d{3,})$/.exec(ref.trim());
  return m ? parseInt(m[1]!, 10) : null;
}

/**
 * Family of a category, derived from existing materials. Null when new or ambiguous.
 * @param category The category.
 * @returns The family, or null.
 */
export function familyFromCategory(category: string): string | null {
  const cat = (category ?? "").trim();
  if (!cat) return null;
  const fams = new Set<string>();
  for (const m of CATALOGUE) if (m.category === cat && MATERIAL_FAMILY[m.ref]) fams.add(MATERIAL_FAMILY[m.ref]!);
  return fams.size === 1 ? [...fams][0]! : null;
}

/**
 * Ref prefix of a family, derived from that family's materials so no list needs maintaining.
 * @param family The family.
 * @returns The prefix, or null.
 */
export function prefixFromFamily(family: string): string | null {
  for (const [ref, fam] of Object.entries(MATERIAL_FAMILY)) if (fam === family) { const p = refPrefix(ref); if (p) return p; }
  return null;
}

/**
 * Next ref in a prefix's series: max(existing numbers, floor − 1) + 1.
 * @param pfx The series prefix.
 * @param existingRefs The refs already taken.
 * @param floor The numbering floor of the series.
 * @returns The next ref.
 */
export function nextRef(pfx: string, existingRefs: readonly string[], floor = M2_RANGE): string {
  let max = floor - 1;
  for (const r of existingRefs) {
    if (pfx && r.startsWith(pfx + "-")) { const n = refNumber(r); if (n != null && n > max) max = n; }
  }
  return `${pfx}-${max + 1}`;
}

/**
 * Default waste coefficient of a category, via the nature of its family prefix.
 * @param category The category.
 * @returns The material nature and its default coefficient; both `null` when the family group has no nature
 *   in the settings (the creation is then blocked, never defaulted to "rigid").
 */
export function defaultCoeffForCategory(category: string): { nature: MaterialNature | null; coeff: number | null } {
  const fam = familyFromCategory(category);
  const pfx = fam ? prefixFromFamily(fam) : "";
  const nature = materialNatureOf(pfx ? `${pfx}-0000` : undefined);
  if (!nature) return { nature: null, coeff: null };
  return { nature, coeff: nature === "none" ? 0 : DEFAULT_WASTE_COEFF[nature] };
}

/** Compatibilities inherited from a family, from the same source as the quote menus. */
export interface FamilyCompat {
  family: string;
  print: MenuItem[];
  cut: MenuItem[];
  lamination: MenuItem[];
  finishing: MenuItem[];
  /** Notably forbidden prints, as a one-line "REF — label" summary. */
  forbiddenPrints: MenuItem[];
}

/**
 * Compatibilities a material of the family inherits, from the same source as the quote menus.
 * @param family The family.
 * @returns The inherited compatibilities, or null when the family is unknown.
 */
export function familyCompatibles(family: string): FamilyCompat | null {
  const f = menusImport().families[family];
  if (!f) return null;
  const label = new Map<string, string>();
  for (const p of PRINTS) label.set(p.code, p.label);
  const allowed = new Set(f.print.map((i) => i.ref));
  const forbiddenPrints = PRINTS.filter((p) => !allowed.has(p.code)).map((p) => ({ ref: p.code, label: `${p.code} — ${label.get(p.code) ?? p.code}` }));
  return { family, print: f.print, cut: f.cut, lamination: f.lamination, finishing: f.finishing, forbiddenPrints };
}

const RE_FORMAT_MM = /^\d{1,6}\s*[x×]\s*\d{1,6}$/;

/**
 * Validates the creation of a material. Called identically by the panel and the import.
 * @param b The material draft.
 * @param ctx The current catalogue state.
 * @returns The blockers, the pending reasons, the informative notices and the normalized material.
 */
export function validateMaterialCreation(b: MaterialDraft, ctx: CreationContext): CreationResult {
  const blockers: string[] = [];
  const pending: string[] = [];
  const informations: string[] = [];
  const category = (b.category ?? "").trim();

  // Category to derived family
  if (!category) blockers.push("engineRefCategoryRequired");
  const isNewCategory = !!category && !ctx.existingCategories.includes(category);
  const derivedFamily = familyFromCategory(category);
  // A known category derives its family; a new one requires an explicit family.
  const family = derivedFamily ?? (b.family17 ?? "").trim();
  if (isNewCategory) pending.push(engineMessage("engineRefNewCategoryPending", { category }));

  const families17 = Object.keys(COMPAT.print ?? {});
  if (!family) blockers.push(isNewCategory ? "engineRefNewCategoryNeedsFamily" : "engineRefFamilyNotFound");
  else if (!families17.includes(family)) blockers.push(engineMessage("engineRefFamilyUnknown", { family }));

  // Auto reference: family prefix plus the next number in the series. The series counts roots
  // reserved by provisional materials, so the auto ref skips them and an explicit ref equal to one
  // of them materializes it rather than breaking the series.
  const roots = ctx.reservedRoots ?? [];
  const pfx = family ? prefixFromFamily(family) : null;
  const expectedRef = pfx ? nextRef(pfx, [...ctx.existingRefs, ...roots]) : "";
  const ref = (b.ref ?? "").trim() || expectedRef; // empty in the panel means auto; import may be explicit
  const materializes = !!ref && roots.includes(ref) && !ctx.existingRefs.includes(ref);
  if (ref) {
    const n = refNumber(ref);
    if (n == null) blockers.push(engineMessage("engineRefFormatExpected", { ref }));
    else if (n < M2_RANGE) blockers.push(engineMessage("engineRefBelowRange", { ref, range: String(M2_RANGE) }));
    if (ctx.existingRefs.includes(ref)) blockers.push(engineMessage("engineRefAlreadyUsedNeverReused", { ref }));
    else if (pfx && expectedRef && ref !== expectedRef && !materializes) blockers.push(engineMessage("engineRefBreaksSeries", { ref, expected: expectedRef }));
    if (pfx && n != null && !ref.startsWith(pfx + "-")) blockers.push(engineMessage("engineRefPrefixExpectedFamily", { ref, prefix: pfx, family }));
  }

  // Identity
  if (!b.itemShort?.trim()) blockers.push("engineRefItemShortRequired");
  if (!b.fullName?.trim()) blockers.push("engineRefFullNameRequired");
  const weight = b.weightGsm == null || b.weightGsm === "" ? null : num(b.weightGsm);
  if (weight == null || !(weight > 0)) blockers.push("engineRefWeightRequired");

  // Sale price
  const pv = b.pvM2 == null || b.pvM2 === "" ? null : num(b.pvM2);
  if (pv == null || !(pv > 0)) blockers.push("engineRefPvRequired");
  const flatFee = b.minFlatFee == null || b.minFlatFee === "" ? null : num(b.minFlatFee);
  if (flatFee == null || flatFee < 0) blockers.push("engineRefFlatFeeRequired");
  const min_unit_price = b.minUnitPrice == null || b.minUnitPrice === "" ? null : num(b.minUnitPrice);
  if (min_unit_price == null || min_unit_price < 0) blockers.push("engineRefMinUnitPriceRequired");
  if (!b.standardPrint?.trim()) blockers.push("engineRefStandardPrintRequired");
  else if (!Object.prototype.hasOwnProperty.call(STD_PRINT_MAP, b.standardPrint)) blockers.push(engineMessage("engineRefStandardPrintUnknown", { print: b.standardPrint, known: Object.keys(STD_PRINT_MAP).join(", ") }));

  // Formats and purchase
  const formats = (b.formats ?? []).map((f) => String(f).trim()).filter(Boolean);
  if (!formats.length) blockers.push("engineRefFormatRequired");
  else {
    const malformed = formats.filter((f) => !RE_FORMAT_MM.test(f));
    if (malformed.length) blockers.push(engineMessage("engineRefFormatMalformed", { formats: malformed.join(", ") }));
    const def = (b.defaultFormat ?? "").trim();
    if (!def) blockers.push("engineRefDefaultFormatRequired");
    else if (!formats.includes(def)) blockers.push(engineMessage("engineRefDefaultFormatMissing", { format: def }));
  }
  if (!b.distributor?.trim()) blockers.push("engineRefDistributorRequired");
  const purchasePa = b.purchasePaM2 == null || b.purchasePaM2 === "" ? null : num(b.purchasePaM2);
  if (purchasePa == null || purchasePa < 0) blockers.push("engineRefPurchasePaRequired");

  // UPZ no longer blocks: the pair is created when persisting
  const withTwinUpz = !!b.stockUpz;
  if (withTwinUpz) {
    const refU = ref + "U";
    if (ctx.existingRefs.includes(refU)) blockers.push(engineMessage("engineRefUpzTwinUsed", { ref: refU }));
    else informations.push(engineMessage("engineRefUpzPairInfo", { ref, twin: refU }));
  }

  // Waste coefficient: family default when empty, specific otherwise
  const enteredCoeff = b.wasteCoeff == null || b.wasteCoeff === "" ? null : Math.max(0, num(b.wasteCoeff));
  const familyNature = pfx ? materialNatureOf(`${pfx}-0000`) : null;
  if (pfx && !familyNature) blockers.push(engineMessage("engineRefUnknownMaterialGroup", { group: pfx }));
  const defaultCoeff = familyNature ? (familyNature === "none" ? 0 : DEFAULT_WASTE_COEFF[familyNature]) : null;
  const wasteCoeff = enteredCoeff == null || defaultCoeff == null ? enteredCoeff : Math.abs(enteredCoeff - defaultCoeff) < 1e-9 ? null : enteredCoeff; // differs from default means specific

  // A missing CRM ref makes the ref inactive; an uncollected PA grid is informative only
  if (!b.refCrm?.trim()) pending.push("engineRefCrmMissingPending");
  if (b.collectedPaGrid === false) informations.push("engineRefPaGridNotCollected");

  const creatable = blockers.length === 0;
  const proposedStatus: "active" | "inactive" = pending.length > 0 ? "inactive" : "active";
  const normalized: NormalizedMaterial | null = creatable
    ? {
        ref,
        name: b.itemShort.trim(),
        fullName: b.fullName.trim(),
        category: category,
        pv: pv!,
        min_flat_fee: flatFee!,
        min_unit_price: min_unit_price!,
        standardPrint: b.standardPrint,
        kg: weight!,
        isMoq: false,
        family17: family,
        // Canonical format style of the referential: "L × H", with the multiplication sign and
        // spaces, matching the historical formats. The old "LxH" form broke referential integrity.
        formats: formats.map((f) => f.replace(/\s*[x×]\s*/, " × ")),
        defaultFormat: b.defaultFormat.trim().replace(/\s*[x×]\s*/, " × "),
        distributor: b.distributor.trim(),
        brand: (b.brand ?? "").trim(),
        purchasePa: purchasePa!,
        wasteCoeff,
        stockUpz: false,
        withTwinUpz,
      }
    : null;

  return { blockers, pending, informations, proposedStatus, creatable, normalized };
}

/**
 * Expands a normalized material into the refs to persist: the standard one alone, or the pair
 * (standard plus "U" twin) for in-house stock. The twin gets ref + "U" and prefixed labels.
 * @param m The normalized material.
 * @returns The material(s) to persist.
 */
export function materialTwins(m: NormalizedMaterial): NormalizedMaterial[] {
  const standard: NormalizedMaterial = { ...m, stockUpz: false, withTwinUpz: false };
  if (!m.withTwinUpz) return [standard];
  const twin: NormalizedMaterial = {
    ...m,
    ref: m.ref + "U",
    name: `UPZ - ${m.name}`,
    fullName: `U-${m.fullName}`,
    stockUpz: true,
    withTwinUpz: false,
  };
  return [standard, twin];
}

/**
 * Context derived from the bound data; the batch import's default.
 * @returns The creation context.
 */
export function embeddedContext(): CreationContext {
  return {
    existingRefs: CATALOGUE.map((m) => m.ref),
    existingCategories: [...new Set(CATALOGUE.map((m) => m.category))],
  };
}

/** Closed lists and context for the panel: selects, distributors, brands, coefficient, auto ref. */
export interface MaterialCreationOptions {
  families17: string[];
  existingCategories: string[];
  distributors: string[];
  brands: string[];
  standardPrint: string[];
  existingRefs: string[];
}

/**
 * Closed lists and context for the creation panel.
 * @returns The options.
 */
export function materialCreationOptions(): MaterialCreationOptions {
  const distributors = new Set<string>();
  const brands = new Set<string>();
  for (const v of Object.values(MATERIAL_REFS)) {
    if (v.supplier) distributors.add(String(v.supplier));
    if (v.brand) brands.add(String(v.brand));
  }
  return {
    families17: Object.keys(COMPAT.print ?? {}),
    existingCategories: [...new Set(CATALOGUE.map((m) => m.category))].sort(),
    distributors: [...distributors].sort(),
    brands: [...brands].sort(),
    standardPrint: Object.keys(STD_PRINT_MAP),
    existingRefs: CATALOGUE.map((m) => m.ref),
  };
}

// Batch validation: calls validateMaterialCreation per row. Uniqueness holds within the batch (a ref
// and its "U" twin become taken for later rows), and a refusal is strictly all-or-nothing.

/** One validated row of a material batch. */
export interface MaterialBatchLine {
  index: number;
  draft: MaterialDraft;
  result: CreationResult;
}

/** Result of a material batch validation. */
export interface BatchResult {
  lines: MaterialBatchLine[];
  /** Refs to persist, in order with UPZ pairs expanded; only when the batch is not blocked. */
  creatable: NormalizedMaterial[];
  refusal: { index: number; ref: string; blockers: string[] }[];
  inactive: { ref: string; pending: string[] }[];
  blocked: boolean;
}

/**
 * Validates a batch of materials, row by row.
 * @param drafts The material drafts.
 * @param baseCtx The catalogue state before the batch.
 * @returns The per-row results and the refs to persist.
 */
export function validateMaterialBatch(drafts: readonly MaterialDraft[], baseCtx: CreationContext): BatchResult {
  const accumulatedRefs = new Set(baseCtx.existingRefs);
  const accumulatedCategories = new Set(baseCtx.existingCategories);
  const lines: MaterialBatchLine[] = [];
  const creatable: NormalizedMaterial[] = [];
  drafts.forEach((draft, index) => {
    const ctx: CreationContext = { existingRefs: [...accumulatedRefs], existingCategories: [...accumulatedCategories] };
    const result = validateMaterialCreation(draft, ctx);
    lines.push({ index, draft, result });
    if (result.creatable && result.normalized) {
      for (const m of materialTwins(result.normalized)) { creatable.push(m); accumulatedRefs.add(m.ref); }
      accumulatedCategories.add(result.normalized.category);
    }
  });
  const refusal = lines.filter((l) => !l.result.creatable).map((l) => ({ index: l.index, ref: l.draft.ref, blockers: l.result.blockers }));
  const inactive = lines
    .filter((l) => l.result.creatable && l.result.proposedStatus === "inactive")
    .map((l) => ({ ref: l.result.normalized!.ref, pending: l.result.pending }));
  return { lines, creatable: refusal.length ? [] : creatable, refusal, inactive, blocked: refusal.length > 0 };
}

// Components (print, cutting, finishing, lamination).
//
// Mirror of materials: a material derives its family and inherits compatibilities, whereas a
// component declares the families where it is allowed. Auto ref per prefix series, PV €/m² (a
// straight cut may be 0), optional minimum unit price and flat fee. No UPZ, format or CRM ref: a
// component is active from creation, since it is priced inside combinations.

/** Component type. These values are persisted in `catalog_items.type`: do not rename them. */
export type ComponentKind = "print" | "cut" | "finishing" | "lamination";

/** COMPAT axis and series prefix for each component type. */
export const COMPONENT_AXIS: Record<ComponentKind, { axis: keyof Compat; prefix: string; labelKey: string }> = {
  print: { axis: "print", prefix: "IMP", labelKey: "engineAxisPrint" },
  cut: { axis: "cut", prefix: "CUT", labelKey: "engineAxisCut" },
  finishing: { axis: "finishing", prefix: "CONF", labelKey: "engineAxisFinishing" },
  lamination: { axis: "lamination", prefix: "PLF", labelKey: "engineAxisLamination" },
};

/** Component draft (panel or batch row). */
export interface ComponentDraft {
  type: ComponentKind;
  /** Ref/code, auto-computed by the panel and optionally explicit on import. Empty means series. */
  ref: string;
  itemShort: string;
  fullName: string;
  /** Bare PV €/m² (≥ 0). */
  pvM2: number | string | null;
  /** Minimum unit price per piece (optional, cumulative with the material's). */
  minUnitPrice?: number | string | null;
  /** Minimum setup flat fee (optional, consolidated once per component). */
  min_flat_fee?: number | string | null;
  /** Families where this component is allowed (at least one), the inverse of a material's declaration. */
  compatibleFamilies: readonly string[];
}

/** Normalized component, ready to persist. */
export interface NormalizedComponent {
  type: ComponentKind;
  axis: keyof Compat;
  code: string;
  label: string;
  fullLabel: string;
  pv: number;
  min_unit_price: number | "";
  min_flat_fee: number | null;
  compatibleFamilies: string[];
}

/** Result of a component creation validation. */
export interface ComponentCreationResult {
  blockers: string[];
  informations: string[];
  creatable: boolean;
  normalized: NormalizedComponent | null;
}

/**
 * All known families (union of the axes), for a component's compatibility declaration.
 * @returns The sorted families.
 */
export function allFamilies(): string[] {
  const s = new Set<string>();
  for (const axisMap of Object.values(COMPAT)) for (const fam of Object.keys(axisMap as object)) s.add(fam);
  return [...s].sort();
}

/**
 * Existing component codes across the four axes, for uniqueness and series.
 * @returns The component codes.
 */
function existingComponentRefs(): string[] {
  const codes: string[] = [];
  for (const p of PRINTS) codes.push(p.code);
  for (const c of CUTS) codes.push(c.code);
  for (const p of LAMINATIONS) codes.push(p.code);
  for (const c of FINISHINGS) if (c.label && /^CONF-/.test(c.code)) codes.push(c.code);
  return codes;
}

/**
 * Component context: taken refs (materials and components) for uniqueness and series.
 * @returns The creation context.
 */
export function componentContext(): CreationContext {
  return { existingRefs: [...CATALOGUE.map((m) => m.ref), ...existingComponentRefs()], existingCategories: [] };
}

/** Closed lists and context for the component creation panel. */
export interface ComponentCreationOptions {
  families: string[];
  existingRefs: string[];
}

/**
 * Closed lists and context for the component creation panel.
 * @returns The options.
 */
export function componentCreationOptions(): ComponentCreationOptions {
  return { families: allFamilies(), existingRefs: [...componentContext().existingRefs] };
}

/**
 * Validates the creation of a component. Called identically by the panel and the import.
 * @param b The component draft.
 * @param ctx The current catalogue state.
 * @returns The blockers, the informative notices and the normalized component.
 */
export function validateComponentCreation(b: ComponentDraft, ctx: CreationContext): ComponentCreationResult {
  const blockers: string[] = [];
  const informations: string[] = [];
  const axisConfig = COMPONENT_AXIS[b.type];
  if (!axisConfig) {
    return { blockers: [engineMessage("engineCompTypeUnknown", { type: String(b.type) })], informations, creatable: false, normalized: null };
  }
  const pfx = axisConfig.prefix;
  const expectedRef = nextRef(pfx, ctx.existingRefs);
  const code = (b.ref ?? "").trim() || expectedRef;

  const n = /^[A-Za-z]+-(\d{3,})$/.exec(code) ? parseInt(code.replace(/^[A-Za-z]+-/, ""), 10) : null;
  if (n == null) blockers.push(engineMessage("engineCompRefFormatExpected", { ref: code, prefix: pfx }));
  else if (n < M2_RANGE) blockers.push(engineMessage("engineRefBelowRange", { ref: code, range: String(M2_RANGE) }));
  if (!code.startsWith(pfx + "-")) blockers.push(engineMessage("engineCompRefPrefixExpected", { ref: code, prefix: pfx, axis: keyParam(axisConfig.labelKey) }));
  if (ctx.existingRefs.includes(code)) blockers.push(engineMessage("engineCompRefAlreadyUsed", { ref: code }));
  else if (code !== expectedRef) blockers.push(engineMessage("engineCompRefBreaksSeries", { ref: code, prefix: pfx, expected: expectedRef }));

  if (!b.itemShort?.trim()) blockers.push("engineRefItemShortRequired");
  if (!b.fullName?.trim()) blockers.push("engineCompFullNameRequired");

  const pv = b.pvM2 == null || b.pvM2 === "" ? null : num(b.pvM2);
  if (pv == null || pv < 0) blockers.push("engineCompPvRequired");
  const min_unit_price = b.minUnitPrice == null || b.minUnitPrice === "" ? null : num(b.minUnitPrice);
  if (min_unit_price != null && min_unit_price < 0) blockers.push("engineCompMinUnitPriceInvalid");
  const flatFee = b.min_flat_fee == null || b.min_flat_fee === "" ? null : num(b.min_flat_fee);
  if (flatFee != null && flatFee < 0) blockers.push("engineCompFlatFeeInvalid");

  const knownFamilies = new Set(allFamilies());
  const families = [...new Set((b.compatibleFamilies ?? []).map((f) => String(f).trim()).filter(Boolean))];
  if (!families.length) blockers.push("engineCompFamilyRequired");
  else {
    const unknown = families.filter((f) => !knownFamilies.has(f));
    if (unknown.length) blockers.push(engineMessage("engineCompFamiliesUnknown", { families: unknown.join(", ") }));
  }

  informations.push(engineMessage("engineCompActiveInfo", { count: families.length }));

  const creatable = blockers.length === 0;
  const normalized: NormalizedComponent | null = creatable
    ? {
        type: b.type,
        axis: axisConfig.axis,
        code,
        label: b.itemShort.trim(),
        fullLabel: b.fullName.trim(),
        pv: pv!,
        min_unit_price: min_unit_price == null ? "" : min_unit_price,
        min_flat_fee: flatFee,
        compatibleFamilies: families,
      }
    : null;
  return { blockers, informations, creatable, normalized };
}

/** One validated row of a component batch. */
export interface ComponentBatchLine {
  index: number;
  draft: ComponentDraft;
  result: ComponentCreationResult;
}

/** Result of a component batch validation. */
export interface ComponentBatchResult {
  lines: ComponentBatchLine[];
  creatable: NormalizedComponent[];
  refusal: { index: number; ref: string; blockers: string[] }[];
  blocked: boolean;
}

/**
 * Validates a batch of components, row by row.
 * @param drafts The component drafts.
 * @param baseCtx The catalogue state before the batch.
 * @returns The per-row results and the components to persist.
 */
export function validateComponentBatch(drafts: readonly ComponentDraft[], baseCtx: CreationContext): ComponentBatchResult {
  const accumulatedRefs = new Set(baseCtx.existingRefs);
  const lines: ComponentBatchLine[] = [];
  const creatable: NormalizedComponent[] = [];
  drafts.forEach((draft, index) => {
    const ctx: CreationContext = { existingRefs: [...accumulatedRefs], existingCategories: [] };
    const result = validateComponentCreation(draft, ctx);
    lines.push({ index, draft, result });
    if (result.creatable && result.normalized) { creatable.push(result.normalized); accumulatedRefs.add(result.normalized.code); }
  });
  const refusal = lines.filter((l) => !l.result.creatable).map((l) => ({ index: l.index, ref: l.draft.ref, blockers: l.result.blockers }));
  return { lines, creatable: refusal.length ? [] : creatable, refusal, blocked: refusal.length > 0 };
}

// Computed indicator of a material's PA keys: counts the existing keys in the workshop grids and
// lists the workshops. A brand-new material has none.
export interface MaterialPaKeys {
  count: number;
  workshops: string[];
}

/**
 * Computed indicator of a material's PA keys.
 * @param ref The material reference.
 * @param grids The workshop PA grids.
 * @returns The number of keys and the workshops carrying them.
 */
export function materialPaKeys(ref: string, grids: readonly { material?: string; workshop?: string }[] = PA_SUPPLIERS): MaterialPaKeys {
  const r = (ref ?? "").trim();
  const matching = grids.filter((g) => g.material === r);
  return { count: matching.length, workshops: [...new Set(matching.map((g) => String(g.workshop)))].sort() };
}

// Preparing a material's keys: the bridge from "the material exists" to "collection is ready to
// fill in". Generates the standard collection keys as material × allowed prints × common cuts,
// with the mode following in-house stock. Compatibility-safe by construction, so never a forbidden
// signature. Each key is an empty row ready to receive the six tiers.

/**
 * Common cuts, which dominate real PA grid usage (setting `commonCutCodes`).
 * @returns The cut codes.
 */
export function commonCuts(): readonly string[] {
  return engineSettings().parameters.commonCutCodes;
}

/** A PA collection key: an empty row ready to receive the six tiers. */
export interface CollectionKey {
  material: string;
  print: string;
  cut: string;
  lamination: string;
  finishing: string;
  mode: PaMode;
}

/**
 * Standard collection keys of a material. Empty when the material has no compatibility family.
 * @param ref The material reference.
 * @param cutCodes The cuts to generate keys for (default: the configured common cuts).
 * @returns The collection keys.
 */
export function materialCollectionKeys(ref: string, cutCodes: readonly string[] = commonCuts()): CollectionKey[] {
  const r = (ref ?? "").trim();
  if (!materialFamilyOf(r)) return [];
  const pricingMode: PaMode = isUpz(r) ? "SUPPLIED" : "INCLUDED";
  const prints = compatibleOptions("print", r, PRINTS.map((p) => p.code));
  const cuts = cutCodes.filter((c) => itemAllowed("cut", r, c));
  const keys: CollectionKey[] = [];
  for (const i of prints) for (const c of cuts) keys.push({ material: r, print: i, cut: c, lamination: "", finishing: "", mode: pricingMode });
  return keys;
}

// On-demand CB- combination: a PA key lives per tuple, so no combination is required for the
// lookup. Creating a CB- combination is proposed when the signature does not exist yet, never
// automatically, and a forbidden signature is refused both on input and on import.

/** Components of a PA key tuple. */
export interface KeySignature {
  material: string;
  print: string;
  cut: string;
  lamination?: string;
  finishing1?: string;
  finishing2?: string;
}

/**
 * Is the signature allowed? Every non-empty axis must be allowed for the material's family.
 * @param s The key signature.
 * @returns True when every component is allowed for the material.
 */
export function isSignatureAllowed(s: KeySignature): boolean {
  const checks: [Parameters<typeof itemAllowed>[0], string | undefined][] = [
    ["print", s.print], ["cut", s.cut], ["lamination", s.lamination], ["finishing1", s.finishing1], ["finishing2", s.finishing2],
  ];
  return checks.every(([axis, v]) => !v || itemAllowed(axis, s.material, v));
}

/**
 * Does a registered combination already exist for this tuple?
 * @param s The key signature.
 * @param combinations The registered combinations.
 * @returns The combination ref, or null.
 */
export function existingCombinationRef(s: KeySignature, combinations: readonly { material: string; print: string; cut: string; lamination: string; finishing1: string; finishing2: string }[]): string | null {
  /**
   * Loose string equality, treating undefined as empty.
   * @param a The first value.
   * @param b The second value.
   * @returns True when equal.
   */
  const eq = (a: string | undefined, b: string) => (a ?? "") === (b ?? "");
  const hit = combinations.find((c) => eq(s.material, c.material) && eq(s.print, c.print) && eq(s.cut, c.cut) && eq(s.lamination, c.lamination) && eq(s.finishing1, c.finishing1) && eq(s.finishing2, c.finishing2));
  return hit ? (hit as { ref?: string }).ref ?? null : null;
}

/**
 * Next CB- ref in the series: highest existing + 1, with no gap.
 * @param combinationRefs The existing combination refs.
 * @returns The next CB- ref.
 */
export function nextCombinationRef(combinationRefs: readonly string[]): string {
  let max = 0;
  for (const r of combinationRefs) { const m = /^CB-(\d+)$/.exec(r); if (m) { const n = parseInt(m[1]!, 10); if (n > max) max = n; } }
  return `CB-${String(max + 1).padStart(4, "0")}`;
}
